// Issue #1251 — the gap between "handled" and actually navigating.
//
// `main.tsx`'s vite:preloadError listener calls `event.preventDefault()` and
// then `window.location.reload()`. The preventDefault is what tells vite's
// `handlePreloadError` not to re-throw:
//
//   function handlePreloadError(err) { ...; if (!e.defaultPrevented) throw err; }
//   return promise.then((res) => { ...; return baseModule().catch(handlePreloadError); });
//
// With the default prevented, that handler returns `undefined`, so the `.catch`
// RESOLVES the import with undefined instead of rejecting it. React.lazy stores
// `_status = Resolved, _result = undefined`, and its production initializer —
// `if(1===a._status)return a._result.default;throw a._result;` — reads .default
// off undefined. That TypeError is Sentry JAVASCRIPT-REACT-11.
//
// ⚠️ The reload is already on its way when that happens. `location.reload()` does
// not stop the current page from executing; React keeps rendering for the few
// hundred ms until navigation commits, and everything it throws in that window
// is a symptom of a problem that is already being fixed.
//
// So this module marks that window. Anything reading it is asking "is the page
// I am on already being replaced?" — and if so, an error is transient rather
// than something to alarm the user about.
//
// ⚠️ MODULE SCOPE ON PURPOSE — do NOT move this to sessionStorage. The flag must
// die with the page. A storage-backed flag would survive the very reload it is
// tracking and suppress the fallback on the next boot, which is the boot that
// would actually be broken if the reload did not fix it.

/**
 * How long after a reload is scheduled we still treat a chunk error as
 * transient.
 *
 * A navigation that is going to happen happens in well under a second. This is
 * generous enough to cover a slow WKWebView resume and short enough that a
 * reload which never lands (offline, killed) falls back to the honest "close
 * Kin and reopen" screen rather than leaving her on a reassuring message
 * forever.
 */
export const RELOAD_IN_FLIGHT_GRACE_MS = 10_000;

let reloadScheduledAt: number | null = null;

/** Called immediately before preventDefault()/reload() in main.tsx. */
export const markChunkReloadInFlight = (now: number = Date.now()): void => {
  reloadScheduledAt = now;
};

/**
 * True while a reload we scheduled is still plausibly about to land.
 *
 * False when we never scheduled one — which includes the case where the 60s
 * loop guard suppressed the reload. That is deliberate: if no reload is coming,
 * she really is stuck, and she should see the screen that says so.
 */
export const isChunkReloadInFlight = (now: number = Date.now()): boolean =>
  reloadScheduledAt !== null && now - reloadScheduledAt < RELOAD_IN_FLIGHT_GRACE_MS;

/** Test-only reset. Not called by app code. */
export const __resetChunkReloadState = (): void => {
  reloadScheduledAt = null;
};
